import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useBluelagoon } from "@bluelagoon";
import { Button } from "@shadcn-in-nx/ui/button";
import { Skeleton } from "@shadcn-in-nx/ui/skeleton";
import { ArrowLeft, Building2, Workflow, GitBranch, ThumbsUp, ThumbsDown, Meh, User, Code2, Coins } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@shadcn-in-nx/utils";
import ConversationFlow from "./ConversationFlow";
import MessageDetailPanel from "./MessageDetailPanel";
import HarnessReportBadge from "./HarnessReportBadge";
import ExportChatButton from "./ExportChatButton";

const getRatingDisplay = (rating) => {
    if (rating === 1) return { icon: ThumbsDown, label: "Bad", color: "text-red-500 bg-red-50" };
    if (rating === 2) return { icon: Meh, label: "Okay", color: "text-yellow-500 bg-yellow-50" };
    if (rating === 3) return { icon: ThumbsUp, label: "Great", color: "text-green-500 bg-green-50" };
    return null;
};

const groupMessages = (messages) => {
    const items = [];
    let currentToolGroup = null;
    let lastAssistantCredits = null;

    for (const msg of messages) {
        if (msg.role === "tool_call" || msg.role === "tool_response") {
            if (!currentToolGroup) {
                currentToolGroup = {
                    type: "tool_group",
                    id: `group-${msg.id}`,
                    items: [],
                    createdAt: msg.createdAt,
                    creditsUsed: lastAssistantCredits,
                };
            }
            currentToolGroup.items.push(msg);
        } else {
            if (currentToolGroup) {
                items.push(currentToolGroup);
                currentToolGroup = null;
            }
            if (msg.role === "human" || msg.role === "assistant") {
                items.push(msg);
                if (msg.role === "assistant") {
                    lastAssistantCredits = msg.creditsUsed || null;
                }
            }
        }
    }
    if (currentToolGroup) items.push(currentToolGroup);
    return items;
};

const getCreditColor = (credits) => {
    if (credits >= 50) return "text-red-600 bg-red-50";
    if (credits >= 25) return "text-yellow-600 bg-yellow-50";
    return "text-green-600 bg-green-50";
};

export default function ChatDetailPage() {
    const { chatId } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [selectedIndex, setSelectedIndex] = useState(0);

    const { call: getStepChatDetail, response: data, isLoading, error } = useBluelagoon({
        method: "getStepChatDetail",
    });

    useEffect(() => {
        if (chatId) {
            getStepChatDetail({ stepChatId: chatId });
            try {
                const views = JSON.parse(localStorage.getItem("admin_chat_views") || "{}");
                views[chatId] = new Date().toISOString();
                localStorage.setItem("admin_chat_views", JSON.stringify(views));
                localStorage.setItem("admin_last_viewed_chat", chatId);
            } catch {}
        }
    }, [chatId]);

    const flowItems = useMemo(() => {
        if (!data?.stepChat?.messages) return [];
        return groupMessages(data.stepChat.messages);
    }, [data?.stepChat?.messages]);

    const totalCredits = useMemo(() => {
        if (!data?.stepChat?.messages) return 0;
        return data.stepChat.messages.reduce((sum, msg) => sum + (msg.creditsUsed || 0), 0);
    }, [data?.stepChat?.messages]);

    if (error) {
        return (
            <div className="p-4 text-red-500">Error: {error.message}</div>
        );
    }

    const chat = data?.stepChat;
    const workflow = chat?.workflow;
    const company = workflow?.app?.company;
    const user = workflow?.app?.user;

    return (
        <div className="h-[calc(100vh-70px)] flex flex-col   ">
            {/* Header */}
            <div className="border-b px-4 py-2 bg-white flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
                        <ArrowLeft className="w-4 h-4 mr-1" /> Back
                    </Button>
                    {workflow?.id && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/admin/workflows/${workflow.id}`)}
                        >
                            <Code2 className="w-4 h-4 mr-1" /> Open Editor
                        </Button>
                    )}
                    <ExportChatButton chatId={chatId} />
                </div>
                {isLoading ? (
                    <Skeleton className="h-5 w-48" />
                ) : (
                    <div className="flex items-center gap-3 text-xs text-gray-600">
                        {company && (
                            <span className="flex items-center gap-1">
                                <Building2 className="w-3 h-3" /> {company.name}
                            </span>
                        )}
                        {workflow && (
                            <span className="flex items-center gap-1">
                                <Workflow className="w-3 h-3" /> {workflow.name}
                            </span>
                        )}
                        {chat?.workflowStep && (
                            <span className="flex items-center gap-1">
                                <GitBranch className="w-3 h-3" /> {chat.workflowStep.name}
                            </span>
                        )}
                        {user && (
                            <span className="flex items-center gap-1">
                                <User className="w-3 h-3" />
                                {user.firstName || user.lastName
                                    ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
                                    : user.email}
                                {(user.firstName || user.lastName) && (
                                    <span className="text-gray-400">({user.email})</span>
                                )}
                            </span>
                        )}
                        {chat?.rating && (() => {
                            const r = getRatingDisplay(chat.rating);
                            const Icon = r.icon;
                            return (
                                <span className={`flex items-center gap-1 px-2 py-0.5 rounded ${r.color}`}>
                                    <Icon className="w-3 h-3" /> {r.label}
                                </span>
                            );
                        })()}
                        {totalCredits > 0 && (
                            <span className={cn("flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium", getCreditColor(totalCredits))}>
                                <Coins className="w-3 h-3" /> {t("adminChats.totalCredits")}: {totalCredits.toFixed(2)}
                            </span>
                        )}
                        <HarnessReportBadge stepChatId={chatId} />
                    </div>
                )}
            </div>

            {/* Main content */}
            <div className="flex-1 flex overflow-hidden">
                {/* Left: Conversation Flow */}
                <div className="w-72 border-r bg-gray-50 overflow-y-auto p-3">
                    <h3 className="font-semibold mb-3 text-sm text-gray-700">Conversation Flow</h3>
                    {isLoading ? (
                        <div className="space-y-2">
                            {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-14" />)}
                        </div>
                    ) : (
                        <ConversationFlow
                            items={flowItems}
                            selectedIndex={selectedIndex}
                            onSelect={setSelectedIndex}
                        />
                    )}
                </div>

                {/* Right: Detail Panel */}
                <div className="flex-1 overflow-y-auto p-3 bg-gray-100">
                    {isLoading ? (
                        <Skeleton className="h-64" />
                    ) : (
                        <MessageDetailPanel item={flowItems[selectedIndex]} />
                    )}
                </div>
            </div>
        </div>
    );
}
